import { useCallback, useEffect, useRef, useState } from 'react';
import PipelineAPI from '../api/pipeline';
import { ReviewProfileView } from '../api/structs';

export interface ReviewProfileState {
  /** Null until the first read lands, and again after a failure. */
  profile: ReviewProfileView | null;
  loading: boolean;
  /** Empty while healthy; the reason the fetch failed otherwise. */
  error: string;
  /** Re-reads the profile (after the template was saved, say). */
  refetch: () => void;
}

const messageOf = (error: unknown): string => {
  if (error instanceof Error && error.message) return error.message;
  if (typeof error === 'string' && error) return error;
  return 'Could not load the reviewer profile.';
};

/**
 * The reviewer's picture of one template, fetched lazily: nothing goes out
 * without a template id, and a template is read ONCE however often the graph is
 * re-mounted around it — the profile is derived from saved state, so re-reading
 * it on every open would be a request per click for an answer that did not
 * change. `refetch()` is the deliberate re-read after a save.
 *
 * A response that lost the race — the template id changed, or the caller
 * unmounted, while it was in flight — is dropped rather than written over newer
 * state.
 */
export function useReviewProfile(templateId?: string): ReviewProfileState {
  const [profile, setProfile] = useState<ReviewProfileView | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [version, setVersion] = useState(0);
  /** The `templateId::version` already fetched, so a re-render does not refetch. */
  const fetchedRef = useRef('');

  useEffect(() => {
    if (!templateId) return undefined;
    const key = `${templateId}::${version}`;
    if (fetchedRef.current === key) return undefined;
    fetchedRef.current = key;

    let cancelled = false;
    let settled = false;
    setLoading(true);
    setError('');

    const load = async () => {
      try {
        const data = await PipelineAPI.getReviewProfile(templateId);
        settled = true;
        if (cancelled) return;
        setProfile(data || null);
      } catch (err) {
        settled = true;
        if (cancelled) return;
        setProfile(null);
        setError(messageOf(err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void load();

    return () => {
      cancelled = true;
      // React 18 StrictMode runs every effect twice in development: mount,
      // immediate cleanup, mount again. Without releasing the key here the
      // second run would see it already claimed, skip the fetch, and leave the
      // first (now cancelled) response as the only one — `loading` would stay
      // true forever. Releasing it only when the request had NOT settled keeps
      // the once-per-key guarantee for every real re-render.
      if (!settled) fetchedRef.current = '';
    };
  }, [templateId, version]);

  const refetch = useCallback(() => setVersion((current) => current + 1), []);

  return { profile, loading, error, refetch };
}

export default useReviewProfile;
